/**
 * Chrome on iOS lays its web view out under its own top toolbar when a page is
 * the FIRST load of a tab that ANOTHER APP opened. The header sits hidden under
 * the toolbar; rotating the phone, switching tabs and back, or (sometimes)
 * loading the page again lays it out correctly.
 *
 * Two shapes were measured on device (docs/viewport-diag.html):
 *   - WebKit reports the viewport growing: handled by the bottom-anchored
 *     100svh shell in src/style/index.css.
 *   - WebKit reports a perfectly normal viewport while the top is still
 *     covered: NOTHING in the DOM can detect it (safe-area insets, screenY,
 *     visualViewport, every rect all read identical to a healthy load), so it
 *     can only be cleared, never compensated for.
 *
 * Two clearing levers, each proven on device against a launch condition the
 * other does not cover:
 *   1. A second navigation at boot (2026-09-07): fixes the COLD launch, where
 *      Chrome was not running and the other app started it. Does nothing for
 *      a new tab opened in an already-running Chrome (issue #249, proved with
 *      a replayed load that still landed displaced).
 *   2. Opening a blank tab and closing it again (2026-09-22): Chrome re-attaches
 *      this tab's view, which is the same class of event as the manual tab
 *      switch users discovered. Fixes the running-Chrome case. It needs a user
 *      gesture (window.open is popup-blocked otherwise), so it runs once, on
 *      the first tap after the hand-off, and the tap keeps its normal effect.
 *   Seven in-page triggers (viewport-fit toggles, tall document + scroll,
 *   initial-scale jiggle, hash / replaceState / pushState, keyboard, element
 *   fullscreen — unavailable on the phone anyway) did nothing.
 *
 * Scope: only loads we can prove another app opened. There are exactly two
 * such entries and we mint both:
 *   1. The Add Device QR (Camera app) — carries `?from=qr`.
 *   2. The Pro Wi-Fi provisioning hand-off: the native Kegtron app finishes
 *      provisioning and opens `/add-device?claim=<pubkeys>` (issue #249).
 *
 * Loop safety: the second navigation always adds the spent marker `ktrl=1`,
 * and a load carrying it never navigates again. The QR marker is dropped by
 * the replay; a claim is load-bearing and kept. The replayed load still knows
 * it came from an app (the spent marker says so), arms the first-tap
 * re-attach, and tidies the marker off the URL without navigating.
 *
 * Every other browser and every other way into the app (typed URL, bookmark,
 * link, back/forward) is untouched; non-Chrome browsers merely have our own
 * markers tidied off the URL.
 */

export const QR_LAUNCH_PARAM = 'from';
export const QR_LAUNCH_VALUE = 'qr';

/** The app hand-off's device-claim param (owned by routes/add-device). */
export const CLAIM_PARAM = 'claim';

/** Spent marker: "this load already IS the second navigation". */
export const RELAYOUT_DONE_PARAM = 'ktrl';
export const RELAYOUT_DONE_VALUE = '1';

/** Delay before the second navigation, ms. Tuned on device; see docs harness. */
export const RELAYOUT_DELAY_MS = 0;

/** How long the blank tab stays open before this one is re-shown, ms. */
export const REATTACH_HOLD_MS = 500;

export function isChromeIos(userAgent) {
	return /CriOS\//.test(userAgent || '');
}

/**
 * Chrome on an iPhone specifically. The displacement comes from the phone
 * layout's collapsing toolbars; iPad Chrome (tab strip, no bottom toolbar)
 * never showed it on device, so the VISIBLE mitigation (banner + first-tap
 * blank tab) is iPhone-only. The invisible boot reload stays for all iOS.
 */
export function isChromeIphone(userAgent) {
	return isChromeIos(userAgent) && /iPhone/.test(userAgent || '');
}

/** True when the URL carries the Add Device QR marker. */
export function isQrLaunch(search) {
	return new URLSearchParams(search || '').get(QR_LAUNCH_PARAM) === QR_LAUNCH_VALUE;
}

/** True when the URL carries a device claim — the native app's hand-off. */
export function isClaimHandoff(search) {
	const claim = new URLSearchParams(search || '').get(CLAIM_PARAM);
	return typeof claim === 'string' && claim.length > 0;
}

/** True for any load we can prove another app opened. */
export function isAppHandoff(search) {
	return isQrLaunch(search) || isClaimHandoff(search);
}

/** True when this load already is the replayed one. */
export function isRelayoutDone(search) {
	return new URLSearchParams(search || '').get(RELAYOUT_DONE_PARAM) === RELAYOUT_DONE_VALUE;
}

/** Append the QR marker to a dashboard URL (used when minting the QR). */
export function withQrLaunchParam(url) {
	const u = new URL(url);
	u.searchParams.set(QR_LAUNCH_PARAM, QR_LAUNCH_VALUE);
	return u.toString();
}

/** The same URL without the QR marker; other params are preserved. */
export function stripQrLaunchParam(href) {
	const u = new URL(href);
	u.searchParams.delete(QR_LAUNCH_PARAM);
	return u.toString();
}

/** The same URL without ANY marker this module owns. `claim` is preserved. */
export function stripLaunchMarkers(href) {
	const u = new URL(href);
	u.searchParams.delete(QR_LAUNCH_PARAM);
	u.searchParams.delete(RELAYOUT_DONE_PARAM);
	return u.toString();
}

/**
 * The URL the second navigation goes to: the QR marker dropped, the claim (if
 * any) kept, and the spent marker added. The result can never satisfy
 * shouldRelayout() again, so a loop is impossible.
 */
export function relayoutTarget(href) {
	const u = new URL(href);
	u.searchParams.delete(QR_LAUNCH_PARAM);
	u.searchParams.set(RELAYOUT_DONE_PARAM, RELAYOUT_DONE_VALUE);
	return u.toString();
}

/**
 * Pure decision: should this page load be replaced by a second navigation?
 * Only a Chrome-iOS load another app launched, and only once.
 */
export function shouldRelayout({ userAgent, search }) {
	return isChromeIos(userAgent) && isAppHandoff(search) && !isRelayoutDone(search);
}

/**
 * Pure decision: should this load re-attach its tab on the user's first tap
 * (and show the banner that says so)? Any Chrome-iPhone load another app
 * opened, including the replayed one. iPad is excluded on purpose.
 */
export function shouldArmReattach({ userAgent, search }) {
	return isChromeIphone(userAgent) && (isAppHandoff(search) || isRelayoutDone(search));
}

/**
 * Open a blank tab and close it again so Chrome re-attaches this tab's view.
 * Must run inside a user gesture or window.open is blocked.
 * @returns {boolean} true if the blank tab was opened
 */
export function reattachViaBlankTab(w) {
	let other = null;
	try {
		other = w.open('about:blank', '_blank');
	} catch (e) {
		other = null;
	}
	if (!other) return false;
	const close = () => {
		try {
			other.close();
		} catch (e) {
			// Already gone.
		}
	};
	w.setTimeout(close, REATTACH_HOLD_MS);
	// If the tap navigated this document away, the timer dies with it; make
	// sure the blank tab does not outlive us.
	w.addEventListener('pagehide', close, { once: true });
	return true;
}

let reattachPending = false;
const reattachListeners = new Set();

/** True while a hand-off load is still waiting for its first-tap re-attach. */
export function isReattachPending() {
	return reattachPending;
}

/** Be told when the pending state changes. Returns an unsubscribe function. */
export function subscribeReattach(listener) {
	reattachListeners.add(listener);
	return () => reattachListeners.delete(listener);
}

function setReattachPending(pending) {
	if (reattachPending === pending) return;
	reattachPending = pending;
	reattachListeners.forEach((listener) => {
		try {
			listener(pending);
		} catch (e) {
			// A listener's failure must not stop the others.
		}
	});
}

/** Test seam: forget any pending re-attach and its subscribers. */
export function resetReattachForTests() {
	reattachPending = false;
	reattachListeners.clear();
}

/**
 * Run the pending re-attach. Must be inside a user gesture. A blocked popup
 * leaves it pending so the next tap retries.
 * @returns {boolean} true if it ran
 */
export function runReattach(w) {
	if (!reattachPending) return false;
	if (!reattachViaBlankTab(w)) return false;
	setReattachPending(false);
	return true;
}

/**
 * Run the re-attach on the first tap anywhere in the document. Capture phase,
 * so it runs inside the gesture before the tap's own handler; the tap is not
 * cancelled and keeps its normal effect. The listener retires once the
 * re-attach has run (or was run from elsewhere, e.g. the hand-off strip).
 */
export function armReattachOnFirstTap(w) {
	setReattachPending(true);
	const doc = w.document;
	const onClick = () => {
		if (!reattachPending || runReattach(w)) {
			doc.removeEventListener('click', onClick, true);
		}
	};
	doc.addEventListener('click', onClick, true);
}

/**
 * Call once, as early as possible in app start-up.
 * @returns {boolean} true if a second navigation was scheduled
 */
export function relayoutChromeIosIfNeeded() {
	if (typeof window === 'undefined') return false;
	const { location, history, navigator } = window;
	if (!isAppHandoff(location.search) && !isRelayoutDone(location.search)) return false;

	const ctx = { userAgent: navigator.userAgent, search: location.search };
	if (shouldArmReattach(ctx)) armReattachOnFirstTap(window);

	if (!shouldRelayout(ctx)) {
		// Nothing more to do at boot: either not Chrome on iOS, or this load
		// already IS the replayed one. Just tidy our own markers off the URL.
		const tidy = stripLaunchMarkers(location.href);
		if (tidy !== location.href) {
			try {
				history.replaceState(history.state, '', tidy);
			} catch (e) {
				// Leave the URL as-is; the markers are inert.
			}
		}
		return false;
	}

	const go = () => location.replace(relayoutTarget(location.href));
	if (RELAYOUT_DELAY_MS > 0) window.setTimeout(go, RELAYOUT_DELAY_MS);
	else go();
	return true;
}
